/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useDispatchLocationList.ts
 🗓️ Created: 2025-12-04
 🧩 Description:
    Hook for managing Dispatch Locations list with DataGrid.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchDispatchLocations } from '../api/dispatchLocationApi';
import type { DispatchLocation } from '../types';

interface UseDispatchLocationListProps {
  domainId?: number;
  onRefreshNeeded?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialColumns?: any[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
}

export function useDispatchLocationList(props: UseDispatchLocationListProps = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const {
    initialColumns = [],
    initialSortBy = 'locationName',
    initialSortOrder = 'asc',
    initialLimit = 10,
  } = props;

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchDispatchLocations(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
          },
          params.search,
          effectiveDomainId
        );

        return {
          data: result.data as DispatchLocation[],
          pagination: result.pagination,
        };
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t('dispatchLocations:messages.loadError');
        showError(message);
        return {
          data: [] as DispatchLocation[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },
    [showError, t, effectiveDomainId]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
    moduleName: 'dispatchLocations',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as DispatchLocation[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

